import React, { useState, useEffect, useMemo } from "react";
import { modalBoxStyle } from "../styles";
import {
  Typography,
  Box,
  Modal,
  Grid,
  IconButton,
  TextField,
  CircularProgress,
  Button,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { debounce } from "lodash";
import inventoryClient from "../../../clients/inventoryClient";
import SearchResultsList from "./components/SearchResultsList";
import { useMutation } from "react-query";

const AddProductToCountModal = ({
  isOpen,
  toggleModalOpen,
  areaCountId,
  stockCountId,
  establishmentId,
  areaId,
  areaName,
  onSuccessCallback,
  skipAddToAreaOption = false,
}) => {
  const [loading, toggleLoading] = useState(false);
  const [searchResults, setSearchResults] = useState();
  const [searchString, setSearchString] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    setSearchString("");
    setSelectedProduct(null);
  }, [isOpen]);

  const getSearchResults = async () => {
    toggleLoading(true);
    const result = await inventoryClient.searchForCountNotAddedProducts({
      establishmentId,
      stockCountId,
      areaCountId,
      searchString,
    });
    setSearchResults(result.results);
    toggleLoading(false);
  };

  useEffect(() => {
    if (!searchString) {
      toggleLoading(false);
      setSearchResults([]);
    } else {
      getSearchResults();
    }

    return () => debouncedSearchHandler.cancel();
  }, [searchString]);

  const handleSearch = (e) => {
    setSearchString(e.target.value);
  };

  const debouncedSearchHandler = useMemo(() => debounce(handleSearch, 300));

  const addProductToAreaCountMutation = useMutation(
    inventoryClient.addProductsToAreaCount
  );

  const addProductToAreaMutation = useMutation(
    inventoryClient.addAreaProducts
  );

  const handleAddProduct = async (formatId, productDisplay) => {
    if (skipAddToAreaOption) {
      // Skip confirmation and add to count only
      try {
        await addProductToAreaCountMutation.mutateAsync({
          areaCountId,
          establishmentId,
          productFormatIds: [formatId],
        });

        onSuccessCallback();
      } catch (error) {
        console.error("Error adding product:", error);
      }
    } else {
      // Show confirmation in modal body
      setSelectedProduct({ formatId, productDisplay });
    }
  };

  const handleConfirmAddPermanent = async () => {
    if (!selectedProduct) return;

    try {
      // First, add to current area count
      await addProductToAreaCountMutation.mutateAsync({
        areaCountId,
        establishmentId,
        productFormatIds: [selectedProduct.formatId],
      });

      // Then, add to area permanently
      await addProductToAreaMutation.mutateAsync({
        payload: {
          establishmentId,
          productFormatIds: [selectedProduct.formatId],
          areaId: areaId,
        },
      });

      setSelectedProduct(null);
      onSuccessCallback();
    } catch (error) {
      console.error("Error adding product:", error);
      setSelectedProduct(null);
    }
  };

  const handleConfirmAddTemporary = async () => {
    if (!selectedProduct) return;

    try {
      // Only add to current area count
      await addProductToAreaCountMutation.mutateAsync({
        areaCountId,
        establishmentId,
        productFormatIds: [selectedProduct.formatId],
      });

      setSelectedProduct(null);
      onSuccessCallback();
    } catch (error) {
      console.error("Error adding product:", error);
      setSelectedProduct(null);
    }
  };

  const handleBackToSearch = () => {
    setSelectedProduct(null);
  };

  const closeModal = () => {
    toggleModalOpen(false);
    toggleLoading(false);
    setSelectedProduct(null);
  };

  return (
    <>
    <Modal
      open={isOpen}
      onClose={closeModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={modalBoxStyle}>
        {addProductToAreaCountMutation?.isLoading || addProductToAreaMutation?.isLoading ? (
          <Grid
            container
            direction="row"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
            }}
          >
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
              <CircularProgress />
            </Grid>
          </Grid>
        ) : selectedProduct ? (
          // Confirmation View
          <>
            <Box sx={{ marginBottom: 4 }}>
              <Grid
                container
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: 2,
                }}
              >
                <Grid item>
                  <Button
                    startIcon={<ChevronLeftIcon />}
                    onClick={handleBackToSearch}
                    sx={{
                      color: (theme) => theme.palette.primary.main,
                      textTransform: "none",
                      fontSize: "16px",
                      padding: 0,
                      minWidth: "auto",
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    Back
                  </Button>
                </Grid>
                <Grid item>
                  <IconButton
                    sx={{ backgroundColor: "transparent" }}
                    onClick={closeModal}
                  >
                    <CloseOutlinedIcon />
                  </IconButton>
                </Grid>
              </Grid>
              <Box>
                <Typography variant="h4">
                  Add to Area Permanently?
                </Typography>
              </Box>
            </Box>
            <Box sx={{ marginBottom: 6 }}>
              <Typography variant="body1">
                Would you like to permanently add <strong>{selectedProduct.productDisplay}</strong> to <strong>{areaName}</strong> for future counts?
              </Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleConfirmAddTemporary}
                  sx={{ padding: 2 }}
                >
                  No, Just This Count
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleConfirmAddPermanent}
                  sx={{ padding: 2 }}
                >
                  Yes, Add Permanently
                </Button>
              </Grid>
            </Grid>
          </>
        ) : (
          // Search View
          <>
            <Box sx={{ marginBottom: 4 }}>
              <Grid
                container
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Grid item>
                  <Typography variant="h4">Add Product to Count</Typography>
                </Grid>
                <Grid item>
                  <IconButton
                    sx={{ backgroundColor: "transparent" }}
                    onClick={closeModal}
                  >
                    <CloseOutlinedIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
            <Box sx={{ marginBottom: 2 }}>
              <Typography variant="body1" color="text.secondary">
                Search by Product Name
              </Typography>
            </Box>
            <Box sx={{ marginBottom: 6 }}>
              <TextField
                size="small"
                variant="outlined"
                fullWidth
                placeholder="Product Name"
                autoComplete="off"
                label="Name"
                InputLabelProps={{ shrink: true }}
                onChange={debouncedSearchHandler}
              />
            </Box>
            <Box>
              <SearchResultsList
                results={searchResults}
                isLoading={loading}
                addProduct={handleAddProduct}
              />
            </Box>
          </>
        )}
      </Box>
    </Modal>
    </>
  );
};

export default AddProductToCountModal;
